import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSimpleAuth } from '../../contexts/SimpleAuthContext';
import './AuthForms.css';

interface PasswordStrength {
  score: number; // 0-3 (weak, medium, strong)
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

const checkPasswordStrength = (password: string): PasswordStrength => {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const metRequirements = Object.values(requirements).filter(Boolean).length;
  let score = 0;
  if (metRequirements >= 5) score = 3; // strong
  else if (metRequirements >= 3) score = 2; // medium
  else if (metRequirements >= 2) score = 1; // weak

  return { score, requirements };
};

export const RegisterForm: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    invitationCode: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecial: false,
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  const { register, error, clearError } = useSimpleAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (formData.password) {
      setPasswordStrength(checkPasswordStrength(formData.password));
    }
  }, [formData.password]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.invitationCode) {
      errors.invitationCode = 'Invitation code is required';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (passwordStrength.score === 0) {
      errors.password = 'Password is too weak';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    clearError();
    
    try {
      await register(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        formData.invitationCode
      );
      setRegistrationSuccess(true);
    } catch (err) {
      // Error is handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2 className="auth-title">Check Your Email</h2>
          <div className="auth-success">
            Registration successful! We've sent a verification link to {formData.email}.
            Please check your email to complete your registration.
          </div>
          <p className="auth-subtitle">
            Didn't receive the email? Check your spam folder or{' '}
            <button 
              className="auth-link" 
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => {/* TODO: Implement resend */}}
            >
              resend verification email
            </button>.
          </p>
          <Link to="/auth/login" className="auth-submit-button" style={{ textAlign: 'center', display: 'block' }}>
            Return to Sign In
          </Link>
        </div>
      </div>
    );
  }

  const getPasswordStrengthClass = () => {
    switch (passwordStrength.score) {
      case 1: return 'weak';
      case 2: return 'medium';
      case 3: return 'strong';
      default: return '';
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2 className="auth-title">Create Your Account</h2>
        <p className="auth-subtitle">Start tracking your lab results today</p>
        
        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={validationErrors.email ? 'error' : ''}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
            />
            {validationErrors.email && (
              <span className="field-error">{validationErrors.email}</span>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '16px' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="firstName">First Name (Optional)</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="John"
                autoComplete="given-name"
              />
            </div>
            
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="lastName">Last Name (Optional)</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Doe"
                autoComplete="family-name"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="invitationCode">
              Invitation Code
              <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
            </label>
            <input
              type="text"
              id="invitationCode"
              name="invitationCode"
              value={formData.invitationCode}
              onChange={handleChange}
              className={validationErrors.invitationCode ? 'error' : ''}
              placeholder="Enter your invitation code"
              autoComplete="off"
            />
            {validationErrors.invitationCode && (
              <span className="field-error">{validationErrors.invitationCode}</span>
            )}
            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
              Don't have a code? <Link to="/" style={{ color: '#3b82f6' }}>Request access</Link>
            </p>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={validationErrors.password ? 'error' : ''}
              placeholder="Create a strong password"
              autoComplete="new-password"
            />
            {validationErrors.password && (
              <span className="field-error">{validationErrors.password}</span>
            )}
            
            {formData.password && (
              <>
                <div className="password-strength">
                  <div className={`password-strength-bar ${getPasswordStrengthClass()}`} />
                </div>
                <div className="password-requirements">
                  <div className={`password-requirement ${passwordStrength.requirements.minLength ? 'met' : 'unmet'}`}>
                    At least 8 characters
                  </div>
                  <div className={`password-requirement ${passwordStrength.requirements.hasUppercase ? 'met' : 'unmet'}`}>
                    One uppercase letter
                  </div>
                  <div className={`password-requirement ${passwordStrength.requirements.hasLowercase ? 'met' : 'unmet'}`}>
                    One lowercase letter
                  </div>
                  <div className={`password-requirement ${passwordStrength.requirements.hasNumber ? 'met' : 'unmet'}`}>
                    One number
                  </div>
                  <div className={`password-requirement ${passwordStrength.requirements.hasSpecial ? 'met' : 'unmet'}`}>
                    One special character
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={validationErrors.confirmPassword ? 'error' : ''}
              placeholder="Confirm your password"
              autoComplete="new-password"
            />
            {validationErrors.confirmPassword && (
              <span className="field-error">{validationErrors.confirmPassword}</span>
            )}
          </div>
          
          <button
            type="submit"
            className="auth-submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/auth/login" className="auth-link">
              Sign in instead
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};